import { ESeriesCategory, ESubscribeMode, IAccountDevice, ICloudService, IPlan, IOrderPreviewArgsItem, ISubDevice, ISubscription, IAdjustmentInfo, IAssociatedDevice } from '@reolink-cloud/interface';
import { productsManager } from '@/lib';
import { SimManager } from '@/lib/simManager';
import { deviceManager } from './deviceManager';
import { valueWithFallbacks } from '@/common/js/utils';

export interface ISubscriptionWithPlan extends ISubscription {
    planInfo?: IPlan;
}

export interface IDeviceInfoFull {
    deviceInfo: IAccountDevice;
    subDeviceInfo?: ISubDevice;
    activeAddOns: ICloudService[];
}

export type TSelectedDevice = { uid: string; videoRetentionDays?: number; }&Record<string, any>;

export default class CheckoutItem {

    public max: number;

    public min: number = 1;

    public qty = 1;

    public lastQty = 1;

    public startedAt: number|null = null;

    public endingAt: number|null = null;

    public adjustmentInfo: IAdjustmentInfo | undefined ;

    public autoRenew = false;

    private tempSelectedDevices: any[]  = [];

    public selectedDevices: TSelectedDevice[] = [];

    public subDevices: TSelectedDevice[] = [];

    public constructor(
        public readonly plan: IPlan,
        public readonly simManager?: SimManager,
        public readonly prevSub?: ISubscriptionWithPlan,
        public mode?: ESubscribeMode
    ) {

        const planMax = this.plan.duration.unit === 'month' ? 36 / this.plan.duration.amount : 10 ;
        this.max = this.plan.limitations.subscriptionMaxPurchaseCycles === 0 ? planMax : this.plan.limitations.subscriptionMaxPurchaseCycles;
        if ( this.plan.limitations.simMaxSubscribeCycles && this.plan.limitations.simMaxSubscribeCycles !== 0 ) {

            this.max = Math.min( this.max, this.plan.limitations.simMaxSubscribeCycles );

        }
        if ( this.isAddOnPlan ) {

            this.max = Math.min( 10 - ( this.simManager?.activeAddOns.length ?? 0 ), this.max );

        }
        if ( this.free ) {

            this.max = 1;

        }
        if ( this.plan.series?.category === ESeriesCategory.CLOUD_STORAGE_PLAN && mode === ESubscribeMode.SWITCH ) {

            this.qty = 0;
            this.min = 0;

        }
        if ( prevSub ) {

            this.selectedDevices = this.subDevices = deviceManager.getSubDeviceList( prevSub );
            this.selectedDevices = this.selectedDevices.filter( device => {

                return deviceManager.checkIfDeviceAvailableInPlan( device.uid, this.plan );

            });

        }

    }

    private get isAddOnPlan(): boolean {

        return this.plan.series?.category === ESeriesCategory.CLOUD_DATA_ADD_ON_PLAN;

    }

    public get lifeTime(): number {

        return this.plan.style['cloud_storage.video_lifetime'] ?? 1;

    }

    public get planVideoQuantity(): number|undefined {

        return this.plan.style['cloud_storage.max_device_quantity'];

    }

    public get availableModels(): string[] {

        if ( this.plan.series?.deviceGroup ) {

            return productsManager.getDeviceModelGroupByKey( this.plan.series.deviceGroup  ).modelsDetails.map( item => item.text );

        }

        return [];

    }

    public get free(): boolean {

        return parseFloat( this.plan.pricing.salePrice )  === 0;

    }

    public get needPreview(): boolean {

        return this.lastQty !== this.qty;

    }

    public get planType(): ESeriesCategory|undefined {

        return this.plan.series?.category;

    }

    public get associateDevices(): IAssociatedDevice[] {

        const finalDevice = [...this.selectedDevices];
        if ( this.simManager?.lockedDevice && this.selectedDevices.every( device => device.uid !== this.simManager?.lockedDevice?.uid )) {

            finalDevice.push({
                uid: this.simManager.lockedDevice.uid,
                videoRetentionDays: this.plan.style['cloud_storage.default_retention_day'] ?? this.lifeTime
            });

        }

        return finalDevice.map( device => {

            return {
                uid: device.uid,
                retentionDays: this.isRichNotificationPlan ? 0 : valueWithFallbacks(
                    ...[
                        device.videoRetentionDays,
                        this.plan.style['cloud_storage.default_retention_day'],
                        this.lifeTime
                    ].filter( num => ( num ?? 0 ) <= this.lifeTime )
                )!
            };

        });

    }

    public get unassociateDevices(): IAssociatedDevice[] {

        const selectedDeviceUids = this.selectedDevices.map( device => device.uid );
        return this.subDevices.filter( device => !selectedDeviceUids.includes( device.uid )
        && this.simManager?.lockedDevice?.uid !== device.uid
        ).map( device => {

            return {
                uid: device.uid,
                retentionDays: this.isRichNotificationPlan ? 0 : valueWithFallbacks(
                    device.videoRetentionDays,
                    this.plan.style['cloud_storage.default_retention_day'],
                    this.lifeTime
                )!
            };

        });

    }

    public getPreviewOrderItem( mode: ESubscribeMode, lang: string ): IOrderPreviewArgsItem {

        return {
            productId: this.plan.id,
            qty: this.qty,
            context: {
                action: mode,
                associateDevices:this.associateDevices,
                unassociateDevices: this.unassociateDevices,
                associateSimCards: this.simManager?.iccid ? [{
                    iccid: this.simManager.iccid
                }] : [],
                lang: lang,
                subscription: this.prevSub && !this.isAddOnPlan ? this.prevSub.id : undefined,
                parentSubscription: this.isAddOnPlan ? this.simManager?.currentSubscription?.id : undefined,
            }
        };

    }

    public getCreateOrderItem( mode: ESubscribeMode, lang: string, autoRenew?: boolean ): Record<string, any> {

        return {
            productId: this.plan.id,
            productType: this.plan.series?.category,
            qty: this.qty,
            context: {
                action: mode,
                lang: lang,
                associateDevices:this.associateDevices,
                unassociateDevices: this.unassociateDevices,
                associateSimCards: this.simManager?.iccid ? [{
                    iccid: this.simManager.iccid
                }] : [],
                parentSubscription: this.isAddOnPlan ? this.simManager?.currentSubscription?.id : undefined,
                autoRenew,
                subscription: this.prevSub && !this.isAddOnPlan ? this.prevSub.id : undefined
            }

        };

    }

    public updateTempSelectedDevices( newVal: any[] ): void {

        this.tempSelectedDevices = newVal;

    }

    public confirmSelectDevices(): void {

        this.selectedDevices = this.tempSelectedDevices;

    }

    public get availableModelCount(): number {

        return deviceManager.getAvailableModelCount( this.availableModels );

    }

    public get hadSelect(): number {

        return this.tempSelectedDevices.length;

    }

    public get changeAssociated(): boolean {

        return this.tempSelectedDevices.some( item => item.status === 'associated' && item.subscription !== this.prevSub?.id );

    }

    public getPlanRenewable( mode: ESubscribeMode ): boolean {

        return this.plan?.status !== 'disabled'
        && !( this.plan?.limitations.userMaxSubscribeTimes === 1 )
        && !( this.plan?.limitations.simMaxSubscribeCycles === 1 )
        && ( mode !== ESubscribeMode.RENEW || ( this.simManager?.currentSubscription?.autoRenewal === true || ( this.simManager?.renewAble ?? true )));

    }

    public resetQty(): void {

        this.qty = this.lastQty;

    }

    public setMode( mode: ESubscribeMode ): void {

        this.mode = mode;
        if ( this.plan.series?.category === ESeriesCategory.CLOUD_STORAGE_PLAN && mode === ESubscribeMode.SWITCH ) {

            this.qty = 0;
            this.min = 0;

        }

    }

    private get isRichNotificationPlan(): boolean {

        return this.planType === ESeriesCategory.CLOUD_RICH_NOTIFICATION_PLAN;

    }

}
